import { Dispatch, SetStateAction } from "react";
import { toast } from "sonner";
import { DictationRefs, DictationSetters } from "./dictation-refs.utils";
import { detectSpeechFromPCM, resampleAudio, convertFloat32ToInt16 } from "./dictation-audio.utils";

const apiUrl = process.env.API_BASE_URL ?? "http://localhost:8000";

export interface UseDictationOptions {
  onTranscript?: Dispatch<SetStateAction<string>>;
  dictationUrl?: string;
  usePCMFormat?: boolean;
  socketParams?: {
    user?: string;
    document?: string | string[];
    current_document?: string;
    [key: string]: string | number | boolean | object | undefined;
  };
}

export function buildWebSocketUrl(options: UseDictationOptions, token: string): string {
  const backendApiUrl = new URL(apiUrl);
  const wsProtocol = backendApiUrl.protocol === "https:" ? "wss:" : "ws:";
  const wsPath = options.dictationUrl ?? "/voice2text/livestreaming";
  const wsUrl = new URL(wsPath, `${wsProtocol}//${backendApiUrl.host}`);
  wsUrl.searchParams.set("token", token);
  return wsUrl.toString();
}

export function validateSecureContext(
  refs: DictationRefs,
  setIsMicroLoading: Dispatch<SetStateAction<boolean>>
): boolean {
  if (!window.isSecureContext) {
    console.error(
      "[DictationDebug] Microphone access requires a secure context (HTTPS). Current origin is not secure."
    );
    toast.error("Microphone Access Denied", {
      description:
        "Microphone requires a secure connection (HTTPS). Please ensure the add-in is served over HTTPS.",
    });
    setIsMicroLoading(false);
    refs.isMicroLoadingRef.current = false;
    return false;
  }
  return true;
}

export function validateAuthToken(
  refs: DictationRefs,
  setIsMicroLoading: Dispatch<SetStateAction<boolean>>
): string | null {
  const token = localStorage.getItem("accessToken")?.trim();
  if (token == null) {
    toast.error("Authentication required", {
      description: "Please log in to use voice features.",
    });
    setIsMicroLoading(false);
    refs.isMicroLoadingRef.current = false;
    return null;
  }
  return token;
}

export function handleTranscriptMessage(
  newTranscript: string,
  setTranscript: Dispatch<SetStateAction<string>>,
  onTranscript?: Dispatch<SetStateAction<string>>
): void {
  console.log("[DictationDebug] handleTranscriptMessage raw:", newTranscript);
  console.log("[DictationDebug] onTranscript callback present:", onTranscript != null);

  const applyText = (text: string) => {
    console.log("[DictationDebug] Applying text to state:", JSON.stringify(text));
    setTranscript((prev) => prev + text);
    if (onTranscript != null) {
      onTranscript((prev) => {
        const updated = prev + text;
        console.log("[DictationDebug] onTranscript updated chatMessage:", JSON.stringify(updated));
        return updated;
      });
    }
  };

  try {
    const parsed: unknown = JSON.parse(newTranscript);
    if (
      typeof parsed === "object" &&
      parsed != null &&
      "text" in parsed &&
      typeof parsed.text === "string"
    ) {
      applyText(parsed.text.trim());
    } else if (typeof parsed === "string") {
      applyText(parsed);
    } else {
      console.warn("[DictationDebug] Parsed but unrecognized format:", parsed);
    }
  } catch (_e) {
    if (newTranscript) {
      console.log("[DictationDebug] JSON parse failed, using raw text");
      applyText(newTranscript);
    }
  }
}

export function setupPCMRecording(
  stream: MediaStream,
  refs: DictationRefs,
  setIsSpeaking: Dispatch<SetStateAction<boolean>>
): void {
  console.log("[DictationDebug] Starting PCM recording...");
  const audioContext = new AudioContext();
  refs.audioContextRef.current = audioContext;
  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(4096, 1, 1);
  refs.audioProcessorRef.current = processor;

  source.connect(processor);
  processor.connect(audioContext.destination);

  const targetSampleRate = 16000;
  const sourceSampleRate = audioContext.sampleRate;
  console.log(
    `[DictationDebug] Audio Sample Rate: Source ${sourceSampleRate}, Target ${targetSampleRate}`
  );

  processor.onaudioprocess = (e: AudioProcessingEvent) => {
    if (refs.socketRef.current?.readyState !== WebSocket.OPEN) return;

    const inputData = e.inputBuffer.getChannelData(0);
    detectSpeechFromPCM(inputData, refs, setIsSpeaking);
    const resampledData = resampleAudio(inputData, sourceSampleRate, targetSampleRate);
    const pcmData = convertFloat32ToInt16(resampledData);
    refs.socketRef.current.send(pcmData.buffer);
  };
}

function initAudioAnalyser(stream: MediaStream, refs: DictationRefs, setters: DictationSetters) {
  const audioContext = new AudioContext();
  refs.audioContextRef.current = audioContext;
  const analyser = audioContext.createAnalyser();
  refs.analyserRef.current = analyser;
  const source = audioContext.createMediaStreamSource(stream);
  source.connect(analyser);
  analyser.fftSize = 256;
  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  const checkAudioLevel = () => {
    if (!refs.isRecordingRef.current && !refs.isMicroLoadingRef.current) return;
    analyser.getByteFrequencyData(dataArray);
    let sum = 0;
    for (let i = 0; i < bufferLength; i++) sum += dataArray[i] ?? 0;
    const average = sum / bufferLength;
    if (average > 10) {
      if (!refs.isSpeakingRef.current) {
        refs.isSpeakingRef.current = true;
        setters.setIsSpeaking(true);
      }
      if (refs.silenceTimerRef.current) {
        clearTimeout(refs.silenceTimerRef.current);
        refs.silenceTimerRef.current = null;
      }
    } else {
      refs.silenceTimerRef.current ??= setTimeout(() => {
        refs.isSpeakingRef.current = false;
        setters.setIsSpeaking(false);
        refs.silenceTimerRef.current = null;
      }, 500);
    }
    requestAnimationFrame(checkAudioLevel);
  };
  requestAnimationFrame(checkAudioLevel);
}

export function setupMediaRecorderRecording(
  stream: MediaStream,
  refs: DictationRefs,
  setters: DictationSetters,
  cleanupAudio: () => void
): void {
  console.log("[DictationDebug] Starting MediaRecorder recording...");
  initAudioAnalyser(stream, refs, setters);

  const mimeType = "audio/webm;codecs=opus";
  const recorderOptions = MediaRecorder.isTypeSupported(mimeType)
    ? { mimeType }
    : { mimeType: "audio/webm" };
  refs.mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);

  refs.checkRecorderDataRef.current = setInterval(() => {
    if (
      refs.mediaRecorderRef.current?.state === "recording" &&
      refs.isMicroLoadingRef.current &&
      !refs.isRecordingRef.current
    ) {
      setters.setIsMicroLoading(false);
      refs.isMicroLoadingRef.current = false;
      setters.setIsRecording(true);
      refs.isRecordingRef.current = true;
    }
  }, 500);

  refs.mediaRecorderRef.current.ondataavailable = (event) => {
    if (event.data.size > 0 && refs.socketRef.current?.readyState === WebSocket.OPEN) {
      refs.socketRef.current.send(event.data);
    }
  };

  refs.mediaRecorderRef.current.onstop = () => {
    if (refs.isRecordingRef.current) setters.setIsConverting(true);
    cleanupAudio();
    setters.setIsRecording(false);
    refs.isRecordingRef.current = false;
    setters.setIsConverting(false);
  };
}
